// Settings → Roles: the granular access matrix. Each role (system or custom)
// is a row in a single full-width list card; admins add a role via the header
// "Add role" button or edit one with the row Edit action — both open the same
// modal containing the name/code fields and a per-MODULE permission matrix.
// The admin role is shown locked (it always has everything) and can't be
// deleted; system roles can be re-permissioned but not deleted.

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { createRole, deleteRole, listPermissions, listRoles, updateRole } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { confirmDialog } from "./ConfirmDialog";
import type { RoleRow } from "../lib/types";
import { Modal } from "./Modal";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "./States";
import { SortTh, useTableSort, type SortAccessor } from "./SortableTable";

type Group = { label: string; codes: string[] };

const ROLE_SORT: Record<string, SortAccessor<RoleRow>> = {
  role: (r) => r.name,
  code: (r) => r.code,
  members: (r) => r.member_count,
  access: (r) => r.permissions.length,
};

/** Module grouping of permission codes (display only — codes are the truth).
 *  Codes not listed here land in "Other" so nothing is ever invisible. */
const MODULES: Group[] = [
  { label: "Tickets", codes: ["ticket.read", "ticket.write", "ticket.request"] },
  { label: "Projects", codes: ["project.read", "project.write", "project.admin"] },
  { label: "Facilities", codes: ["facility.read", "facility.write", "facility.admin"] },
  { label: "Assets", codes: ["asset.read", "asset.write"] },
  { label: "Knowledge Base", codes: ["kb.read", "kb.write", "kb.approve"] },
  { label: "Change Management", codes: ["change.read", "change.write", "change.admin"] },
  { label: "Dashboards", codes: ["dashboard.read"] },
  { label: "Reports", codes: ["report.read"] },
  { label: "Goals", codes: ["goal.read", "goal.write"] },
  { label: "Automations", codes: ["automation.admin"] },
  { label: "Forms", codes: ["form.admin"] },
  { label: "Administration", codes: ["user.admin", "settings.admin"] },
];

/** Short human label for a permission code. For tickets specifically the labels
 *  spell out the two capabilities the product centers on: "Work tickets"
 *  (technician) and "Request tickets" (requester). */
function permLabel(code: string): string {
  const action = code.split(".")[1] ?? code;
  const isTicket = code.startsWith("ticket.");
  switch (action) {
    case "read": return isTicket ? "View tickets" : "View";
    case "write": return isTicket ? "Work tickets" : "Manage";
    case "request": return "Request tickets";
    case "approve": return "Approve";
    case "admin": return "Admin";
    default: return action;
  }
}

export function RolesTab() {
  const rolesApi = useApi((signal) => listRoles(signal), []);
  const permsApi = useApi((signal) => listPermissions(signal), []);
  const roles = rolesApi.data?.roles ?? [];
  const allCodes = useMemo(
    () => (permsApi.data?.permissions ?? []).map((p) => p.code),
    [permsApi.data],
  );

  // Any catalog codes not covered by MODULES still get a column group.
  const groups = useMemo<Group[]>(() => {
    const known = new Set(MODULES.flatMap((m) => m.codes));
    const extra = allCodes.filter((c) => !known.has(c));
    return extra.length ? [...MODULES, { label: "Other", codes: extra }] : MODULES;
  }, [allCodes]);

  const { rows: sortedRoles, sort, toggle } = useTableSort(roles, ROLE_SORT);

  // null = closed, "new" = add, role = edit.
  const [editing, setEditing] = useState<RoleRow | "new" | null>(null);

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h3>Roles &amp; module access</h3>
          <div className="btn-row" style={{ alignItems: "center" }}>
            <span className="faint">{roles.length} role{roles.length === 1 ? "" : "s"}</span>
            <button className="btn small primary" onClick={() => setEditing("new")}>Add role</button>
          </div>
        </div>
        <div className="card-body tight">
          <p className="sub" style={{ margin: "10px 14px" }}>
            Grant each role access per module. <strong>View</strong> shows the module,{" "}
            <strong>Manage</strong> allows changes. For tickets, <strong>Work tickets</strong> lets a
            technician handle the queue, while <strong>Request tickets</strong> lets a requester open
            and follow their own tickets without seeing the queue. The admin role always has full
            access.
          </p>
          {rolesApi.loading || permsApi.loading ? (
            <Loading label="Loading roles…" />
          ) : rolesApi.error ? (
            <ErrorState error={rolesApi.error} onRetry={rolesApi.reload} />
          ) : roles.length === 0 ? (
            <EmptyState>No roles defined.</EmptyState>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <SortTh field="role" sort={sort} onSort={toggle}>Role</SortTh>
                  <SortTh field="code" sort={sort} onSort={toggle}>Code</SortTh>
                  <SortTh field="members" sort={sort} onSort={toggle} className="num">Members</SortTh>
                  <SortTh field="access" sort={sort} onSort={toggle}>Access</SortTh>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sortedRoles.map((r) => (
                  <RoleRow key={r.id} role={r} onEdit={() => setEditing(r)} onChanged={rolesApi.reload} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing ? (
        <RoleModal
          role={editing === "new" ? null : editing}
          groups={groups}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            rolesApi.reload();
          }}
        />
      ) : null}
    </>
  );
}

/** One role row: name/code/member count + a compact access summary; Edit/Delete. */
function RoleRow({ role, onEdit, onChanged }: { role: RoleRow; onEdit: () => void; onChanged: () => void }) {
  const deleteMut = useMutation(deleteRole);
  const locked = role.code === "admin";

  return (
    <tr>
      <td>
        <strong>{role.name}</strong>{" "}
        {role.is_system ? <span className="pill">system</span> : null}
      </td>
      <td className="mono faint">{role.code}</td>
      <td className="num">{role.member_count}</td>
      <td className="faint">
        {locked ? "Full access" : `${role.permissions.length} permission${role.permissions.length === 1 ? "" : "s"}`}
        {!locked && role.grants_access === 0 ? (
          <span className="pill" style={{ marginLeft: 6 }} title="Members can't sign in to the tenant app">
            no sign-in
          </span>
        ) : null}
      </td>
      <td className="row-actions">
        <button className="btn small" onClick={onEdit} disabled={deleteMut.pending}>
          {locked ? "View" : "Edit"}
        </button>
        {!role.is_system ? (
          <button
            className="btn small danger"
            disabled={deleteMut.pending}
            onClick={() =>
              void confirmDialog({ message: `Delete the "${role.name}" role?`, danger: true }).then((yes) => {
                if (yes) void deleteMut.run(role.id).then((ok) => ok && onChanged());
              })
            }
          >
            Delete
          </button>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * Add/Edit role modal — null role = add. Holds name + code (create only) and
 * the per-module permission matrix. The admin role is locked (read-only matrix,
 * no save). Editing a role sends its permissions; create sends code/name and
 * the initial permission set.
 */
function RoleModal({
  role,
  groups,
  onClose,
  onSaved,
}: {
  role: RoleRow | null;
  groups: Group[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const locked = role?.code === "admin";
  const [name, setName] = useState(role?.name ?? "");
  const [code, setCode] = useState(role?.code ?? "");
  // Whether holding this role grants tenant-app sign-in. New roles default to
  // granting access; admin is always true.
  const [grantsAccess, setGrantsAccess] = useState<boolean>(role ? role.grants_access !== 0 : true);
  const [selected, setSelected] = useState<Set<string>>(new Set(role?.permissions ?? []));
  const createMut = useMutation(createRole);
  const updateMut = useMutation(updateRole);
  const pending = createMut.pending || updateMut.pending;
  const err = createMut.error || updateMut.error;

  useEffect(() => {
    setSelected(new Set(role?.permissions ?? []));
  }, [role]);

  function toggle(c: string) {
    if (locked) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (locked) {
      onClose();
      return;
    }
    if (role) {
      const res = await updateMut.run(role.id, { permissions: [...selected], grants_access: grantsAccess });
      if (res) onSaved();
    } else {
      if (!name.trim() || !code.trim()) return;
      const res = await createMut.run({ code: code.trim(), name: name.trim(), permissions: [...selected] });
      if (!res) return;
      // grants_access isn't part of create — set it in a follow-up patch when the
      // admin chose to make a non-access role.
      if (!grantsAccess) await updateMut.run(res.id, { grants_access: false });
      onSaved();
    }
  }

  const title = role ? (locked ? `Role: ${role.name}` : `Edit role: ${role.name}`) : "Add role";

  return (
    <Modal
      title={title}
      onClose={onClose}
      wide
      footer={
        locked ? (
          <button className="btn primary" onClick={onClose}>Close</button>
        ) : (
          <>
            <button className="btn" onClick={onClose} disabled={pending}>Cancel</button>
            <button
              className="btn primary"
              type="submit"
              form="role-form"
              disabled={pending || (!role && (!name.trim() || !code.trim()))}
            >
              {pending ? <Spinner label="Saving…" /> : role ? "Save changes" : "Create role"}
            </button>
          </>
        )
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      {locked ? (
        <p className="sub" style={{ marginTop: 0 }}>
          The admin role always has full access and can't be edited.
        </p>
      ) : null}
      <form id="role-form" onSubmit={onSubmit}>
        {!role ? (
          <div className="row">
            <label className="field">
              <span className="label">Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!code) setCode(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-"));
                }}
                placeholder="e.g. Registrar Staff"
                required
              />
            </label>
            <label className="field">
              <span className="label">Code</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                placeholder="registrar-staff"
                required
              />
            </label>
          </div>
        ) : null}
        <label className="role-perm" style={{ display: "flex", gap: 8, alignItems: "center", margin: "4px 0 14px" }}>
          <input
            type="checkbox"
            checked={locked || grantsAccess}
            disabled={locked}
            onChange={(e) => setGrantsAccess(e.target.checked)}
          />
          <span>
            <strong>Can sign in to the tenant app</strong>
            <span className="hint" style={{ display: "block" }}>
              When off, members of this role are treated as portal/no-access unless a per-user
              override allows them. Use for portal-only or directory-only roles.
            </span>
          </span>
        </label>
        <div className="role-matrix">
          {groups.map((g) => (
            <fieldset className="role-module" key={g.label}>
              <legend>{g.label}</legend>
              {g.codes.map((c) => (
                <label className="role-perm" key={c} title={c}>
                  <input
                    type="checkbox"
                    checked={locked || selected.has(c)}
                    disabled={locked}
                    onChange={() => toggle(c)}
                  />
                  {permLabel(c)}
                </label>
              ))}
            </fieldset>
          ))}
        </div>
      </form>
    </Modal>
  );
}
